if (!customElements.get('custom-size-calculator')) {
  customElements.define('custom-size-calculator', class CustomSizeCalculator extends HTMLElement {
    constructor() {
      super();

      this.toggle = this.querySelector('.custom-size-calculator__toggle');
      this.panel = this.querySelector('.custom-size-calculator__panel');
      this.widthInput = this.querySelector('[data-role="width"]');
      this.heightInput = this.querySelector('[data-role="height"]');
      this.useCheckbox = this.querySelector('[data-role="use-custom"]');
      this.estimateEl = this.querySelector('[data-role="estimate"]');
      this.errorEl = this.querySelector('[data-role="error"]');
      this.propSize = this.querySelector('[data-role="prop-size"]');
      this.propPrice = this.querySelector('[data-role="prop-price"]');
      this.propNote = this.querySelector('[data-role="prop-note"]');

      this.pricePerSqmUnder = parseFloat(this.dataset.pricePerSqmUnder) || 0;
      this.pricePerSqmOver = parseFloat(this.dataset.pricePerSqmOver) || 0;
      this.tierThresholdSqm = parseFloat(this.dataset.tierThreshold) || Infinity;
      this.minWidth = parseFloat(this.dataset.minWidth) || 0;
      this.maxWidth = parseFloat(this.dataset.maxWidth) || Infinity;
      this.minHeight = parseFloat(this.dataset.minHeight) || 0;
      this.maxHeight = parseFloat(this.dataset.maxHeight) || Infinity;
      this.currentEstimate = null;

      try {
        this.formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        });
      } catch (e) {
        this.formatter = null;
      }

      this.toggle.addEventListener('click', this.onToggle.bind(this));
      this.widthInput.addEventListener('input', this.onInputChange.bind(this));
      this.heightInput.addEventListener('input', this.onInputChange.bind(this));
      this.useCheckbox.addEventListener('change', this.onUseChange.bind(this));
    }

    onToggle() {
      const isOpen = !this.panel.hidden;
      this.panel.hidden = isOpen;
      this.toggle.setAttribute('aria-expanded', String(!isOpen));
    }

    onInputChange() {
      this.updateEstimate();
    }

    onUseChange() {
      const useCustom = this.useCheckbox.checked;

      if (useCustom) {
        const validation = this.validate();
        if (!validation.valid) {
          this.useCheckbox.checked = false;
          this.showError(validation.message);
          return;
        }
      }

      this.showError('');
      this.setPropertiesDisabled(!useCustom);
    }

    formatMoney(amount) {
      if (this.formatter) return this.formatter.format(amount);
      return `${this.dataset.currency || ''} ${amount.toFixed(2)}`.trim();
    }

    validate() {
      const width = parseFloat(this.widthInput.value);
      const height = parseFloat(this.heightInput.value);

      if (!width || !height) {
        return { valid: false, message: 'Enter both a width and a length in centimeters.' };
      }
      if (width < this.minWidth || width > this.maxWidth) {
        return { valid: false, message: `Width must be between ${this.minWidth} and ${this.maxWidth} cm.` };
      }
      if (height < this.minHeight || height > this.maxHeight) {
        return { valid: false, message: `Length must be between ${this.minHeight} and ${this.maxHeight} cm.` };
      }
      return { valid: true, width, height };
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    rateForArea(areaSqm) {
      return areaSqm < this.tierThresholdSqm ? this.pricePerSqmUnder : this.pricePerSqmOver;
    }

    updateEstimate() {
      const width = parseFloat(this.widthInput.value);
      const height = parseFloat(this.heightInput.value);

      if (!width || !height || width <= 0 || height <= 0) {
        this.estimateEl.textContent = '';
        this.currentEstimate = null;
        if (this.useCheckbox.checked) {
          this.useCheckbox.checked = false;
          this.setPropertiesDisabled(true);
        }
        return;
      }

      const areaSqm = (width * height) / 10000;
      const rate = this.rateForArea(areaSqm);
      const price = areaSqm * rate;
      this.currentEstimate = { width, height, areaSqm, price };

      this.estimateEl.textContent = `Estimated price: ${this.formatMoney(price)} for ${width} × ${height} cm (${areaSqm.toFixed(2)} m² at ${this.formatMoney(rate)}/m²)`;

      if (this.useCheckbox.checked) {
        this.updateProperties();
      }
    }

    updateProperties() {
      if (!this.currentEstimate) return;
      const { width, height, price } = this.currentEstimate;

      if (this.propSize) this.propSize.value = `${width} x ${height} cm`;
      if (this.propPrice) this.propPrice.value = `${this.formatMoney(price)} (estimate — to be confirmed)`;
    }

    setPropertiesDisabled(disabled) {
      [this.propSize, this.propPrice, this.propNote].forEach((el) => {
        if (el) el.disabled = disabled;
      });
      if (!disabled) this.updateProperties();
    }
  });
}
